import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getNiceDate, roundDecimals, TimeBucketUnits } from '@iotv/datamodel-core';
import { SortOrder } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { NetWorkDAO } from '../../../data/NetworkDAO';
import { useModularTimePeriodSelector } from '../../../hooks/useModularTimePeriodSelector';
import { StripedDataGrid } from '../../factories/DataGrid';
import { signalAttributeColor } from '../../factories/DataGrid/cellFormatters/rangeColors';
import { SimpleIOModal } from '../../io/VOBbased/SimpleIOModal';
import { IOType } from '../../io/types';
const now = Date.now();
const getGatewayDetails = async (gatewaySk, startDay, endDay) => {
    const query = {
        units: TimeBucketUnits.DAY.valueOf(),
        duration: 1,
        fromDateValue: startDay,
        toDateValue: endDay,
        sortOrder: SortOrder.DESCENDING,
        gatewaySks: [gatewaySk],
    };
    const { data } = await NetWorkDAO.geGatewayMetricsFromTimestream(query);
    if (data) {
        return data;
    }
};
const columns = [
    { field: 'bucketStart', headerName: 'Start Period', width: 200, valueFormatter: (value) => getNiceDate(value) },
    { field: 'bucketEnd', headerName: 'End Period', width: 200, valueFormatter: (value) => getNiceDate(value) },
    { field: 'currentRate', headerName: 'Rate', width: 75, valueFormatter: (value) => roundDecimals(value, 2) },
    { field: 'devices', headerName: 'Devices', width: 75, valueFormatter: (value) => roundDecimals(value, 2) },
    { field: 'downlinks', headerName: 'Downlinks', width: 90 },
    {
        field: 'droppedMsgs',
        headerName: 'Dropped',
        headerAlign: 'center',
        width: 75,
        align: 'center',
        renderCell: (params) => signalAttributeColor({ ...params, field: 'dropped' }),
    },
    { field: 'messagesPerPeriodMax', headerName: 'Max. Message Per Period', width: 200 },
    {
        field: 'rssiMean',
        headerName: 'Average RSSI',
        headerAlign: 'center',
        width: 110,
        align: 'center',
        valueFormatter: (value) => roundDecimals(value, 1),
        renderCell: (params) => signalAttributeColor({ ...params, field: 'rssi' }),
    },
    {
        field: 'rssiStdDev',
        headerName: 'RSSI Std. Dev.',
        headerAlign: 'center',
        width: 110,
        align: 'center',
        valueFormatter: (value) => roundDecimals(value, 1),
        renderCell: (params) => signalAttributeColor({ ...params, field: 'rssi' }),
    },
    {
        field: 'sfMean',
        headerName: 'Average SF',
        headerAlign: 'center',
        width: 100,
        align: 'center',
        valueFormatter: (value) => roundDecimals(value, 1),
        renderCell: (params) => signalAttributeColor({ ...params, field: 'sf' }),
    },
    {
        field: 'snrMean',
        headerName: 'Average SNR',
        headerAlign: 'center',
        width: 100,
        align: 'center',
        valueFormatter: (value) => roundDecimals(value, 1),
        renderCell: (params) => signalAttributeColor({ ...params, field: 'snr' }),
    },
    {
        field: 'snrStdDev',
        headerName: 'SNR Std. Dev.',
        headerAlign: 'center',
        width: 110,
        align: 'center',
        valueFormatter: (value) => roundDecimals(value, 1),
        renderCell: (params) => signalAttributeColor({ ...params, field: 'snr' }),
    },
    {
        field: 'totalAirTimeSecs',
        headerName: 'Airtime',
        headerAlign: 'center',
        width: 75,
        align: 'center',
        valueFormatter: (value) => roundDecimals(value, 3),
        renderCell: (params) => signalAttributeColor({ ...params, field: 'consumedAirtimeSecs' }),
    },
    { field: 'uplinks', headerName: 'Uplinks', width: 100 },
];
const columnFields = columns.map((column) => column.field);
const GatewayRecordsTab = (props) => {
    const { gateway } = props;
    const [combineData, setCombinedData] = useState([]);
    const { components, fromDateValue, toDateValue } = useModularTimePeriodSelector({
        componentVariant: 'DatePicker',
        rangeUnit: 'days',
        rangeLength: 7,
        allowedRangeUnits: ['days', 'hours'],
        behaviourFlags: { preventPeriodShiftIntoFuture: true },
        initialToTime: dayjs(now) ?? undefined,
        requireConfirmation: true,
    });
    const { fromPicker, toPicker, confirmButton } = components;
    useEffect(() => {
        getGatewayDetails(gateway.sk, fromDateValue, toDateValue).then((res) => {
            if (res)
                setCombinedData(res);
        });
    }, [fromDateValue, toDateValue]);
    const formatGatewayBuckets = (record) => {
        const { bucketEnd, bucketStart, id } = record;
        const uniqueId = `${bucketStart}-${bucketEnd}-${id}`;
        return { ...record, id: uniqueId, type: 'BucketedData' };
    };
    const gatewayBuckedProps = {
        columns,
        rows: combineData.map(formatGatewayBuckets),
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        density: 'compact',
        rowHeight: 32,
        hideFooter: combineData.length <= 100,
        hideFooterPagination: combineData.length <= 100,
        sx: {
            [`& .MuiDataGrid-cell`]: {
                ['&[data-field="droppedMsgs"], &[data-field="rssiMean"], &[data-field="rssiStdDev"], &[data-field="sfMean"], &[data-field="snrMean"], &[data-field="snrStdDev"], &[data-field="totalAirTimeSecs"]']: {
                    padding: 0,
                },
            },
        },
    };
    return (_jsxs(Stack, { gap: 2, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", gap: 1, children: [_jsx(Typography, { variant: "h6", children: "Gateway Records" }), _jsxs(Stack, { direction: "row", gap: 1, children: [combineData.length > 0 && (_jsx(SimpleIOModal, { allowIOTypeToggle: false,
                                initialIOType: IOType.EXPORT,
                                columnIndexedKeys: columnFields,
                                plainObjects: combineData.map(formatGatewayBuckets),
                                setPlainObjectsInParent: () => { },
                                name: 'Export Bucketed History' })), confirmButton, fromPicker, toPicker] })] }), _jsx(StripedDataGrid, { ...gatewayBuckedProps })] }));
};
export default GatewayRecordsTab;
